import SocialFollow from '../social-follow/SocialFollow';

export type InstagramFollowProps = {
    instagramUrl?: string;
    profileName?: string;
    handle?: string;
    title?: string;
    description?: string;
    buttonLabel?: string;
    followerCount?: string;
    className?: string;
    darkMode?: boolean;
    accentColor?: string;
    openInNewTab?: boolean;
};

const InstagramMark = ({
    className,
    color = 'currentColor',
    glyphColor = '#FFFFFF',
}: {
    className?: string;
    color?: string;
    glyphColor?: string;
}) => (
    <svg
        className={className}
        viewBox="0 0 48 48"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="4" y="4" width="40" height="40" rx="12" fill={color} />
        <rect
            x="13.5"
            y="13.5"
            width="21"
            height="21"
            rx="7"
            fill="none"
            stroke={glyphColor}
            strokeWidth="3.5"
        />
        <circle cx="24" cy="24" r="5.2" fill="none" stroke={glyphColor} strokeWidth="3.5" />
        <circle cx="31.5" cy="16.8" r="2" fill={glyphColor} />
    </svg>
);

const InstagramFollowGraphic = ({
    accentColor,
    darkMode,
}: {
    accentColor: string;
    darkMode: boolean;
}) => {
    const panelFill = darkMode ? '#0F172A' : '#FFF1F5';
    const softFill = darkMode ? '#1E293B' : '#FFE4E6';
    const textFill = darkMode ? '#CBD5E1' : '#64748B';
    const cardFill = darkMode ? '#111827' : '#FFFFFF';

    return (
        <svg
            className="h-full min-h-[210px] w-full"
            viewBox="0 0 360 260"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect x="16" y="18" width="328" height="224" rx="8" fill={panelFill} />

            <rect x="60" y="46" width="104" height="104" rx="30" fill={accentColor} />
            <rect
                x="82"
                y="68"
                width="60"
                height="60"
                rx="18"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="7"
            />
            <circle cx="112" cy="98" r="14" fill="none" stroke="#FFFFFF" strokeWidth="7" />
            <circle cx="134" cy="76" r="4" fill="#FFFFFF" />

            <rect x="188" y="62" width="82" height="12" rx="6" fill={softFill} />
            <rect x="188" y="88" width="120" height="12" rx="6" fill={textFill} opacity=".65" />
            <rect x="188" y="112" width="92" height="12" rx="6" fill={textFill} opacity=".4" />

            <g transform="translate(48 164)">
                <rect width="150" height="46" rx="8" fill={cardFill} />
                <rect x="10" y="8" width="36" height="30" rx="5" fill={softFill} />
                <circle cx="37" cy="16" r="4" fill={accentColor} opacity=".65" />
                <path d="m14 34 9-10 7 7 5-5 7 8H14Z" fill={accentColor} opacity=".8" />
                <path
                    d="M58 17.5c0-3.2 4.2-4.4 6-1.8 1.8-2.6 6-1.4 6 1.8 0 3-3.4 5.4-6 7.5-2.6-2.1-6-4.5-6-7.5Z"
                    fill={accentColor}
                />
                <rect x="78" y="15" width="54" height="7" rx="3.5" fill={textFill} opacity=".65" />
                <rect x="58" y="29" width="62" height="7" rx="3.5" fill={textFill} opacity=".35" />
            </g>

            <g transform="translate(212 142)">
                <rect width="92" height="70" rx="8" fill={cardFill} />
                <rect x="14" y="12" width="26" height="20" rx="4" fill={softFill} />
                <rect x="48" y="12" width="30" height="20" rx="4" fill={accentColor} opacity=".2" />
                <rect
                    x="14"
                    y="39"
                    width="30"
                    height="19"
                    rx="4"
                    fill={accentColor}
                    opacity=".16"
                />
                <rect x="52" y="39" width="26" height="19" rx="4" fill={softFill} />
                <path d="m19 29 6-7 5 5 4-4 3 6H19Z" fill={accentColor} opacity=".7" />
                <circle cx="70" cy="19" r="4" fill={accentColor} opacity=".72" />
            </g>

            <rect x="286" y="34" width="44" height="44" rx="13" fill={accentColor} />
            <rect
                x="296"
                y="44"
                width="24"
                height="24"
                rx="7"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="3"
            />
            <circle cx="308" cy="56" r="5.5" fill="none" stroke="#FFFFFF" strokeWidth="3" />
            <circle cx="316" cy="48" r="1.7" fill="#FFFFFF" />
            <rect x="280" y="28" width="56" height="56" rx="18" fill={accentColor} opacity=".14" />
        </svg>
    );
};

const InstagramFollow = ({
    instagramUrl = 'https://www.instagram.com/',
    profileName = 'Our Instagram profile',
    handle,
    title = 'Follow us on Instagram',
    description = 'See our latest photos, reels, behind-the-scenes moments, and new updates from the team.',
    buttonLabel = 'Follow on Instagram',
    followerCount,
    className = '',
    darkMode = false,
    accentColor = '#E1306C',
    openInNewTab = true,
}: InstagramFollowProps) => (
    <SocialFollow
        platformName="Instagram"
        profileUrl={instagramUrl}
        profileName={profileName}
        handle={handle}
        title={title}
        description={description}
        buttonLabel={buttonLabel}
        followerCount={followerCount}
        className={className}
        darkMode={darkMode}
        accentColor={accentColor}
        openInNewTab={openInNewTab}
        logo={<InstagramMark className="h-11 w-11 flex-shrink-0" color={accentColor} />}
        buttonLogo={
            <InstagramMark
                className="h-5 w-5 flex-shrink-0"
                color="#FFFFFF"
                glyphColor={accentColor}
            />
        }
        graphic={<InstagramFollowGraphic accentColor={accentColor} darkMode={darkMode} />}
    />
);

export default InstagramFollow;
